import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, Link as ReactLink } from 'react-router-dom';

import {
	Alert,
	AlertDescription,
	AlertIcon,
	AlertTitle,
	Box,
	Breadcrumb,
	BreadcrumbItem,
	Button,
	Center,
	Container,
	Flex,
	Spinner,
	Text,
	Wrap,
	WrapItem,
	useBreakpointValue,
} from '@chakra-ui/react';

import { ChevronRightIcon } from '@chakra-ui/icons';
import { IoIosCheckmark } from 'react-icons/io';

import ProductCard from '../components/ProductCard';
import { getProducts } from '../redux/actions/productActions';
import { getUserFavorites } from '../redux/actions/userActions';
import { setFavoritesUpdateFlag } from '../redux/slices/user';

const ProductsScreen = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const { category, pageNumber = 1, keyword = '' } = useParams();

	const bgPosition = useBreakpointValue({
		base: '20% 40%',
		md: '50% 50%',
	});

	const [filteredProducts, setFilteredProducts] = useState([]);
	const [selectedType, setSelectedType] = useState(null);
	const [selectedCategory, setSelectedCategory] = useState(null);

	const { loading, error, products } = useSelector(
		(state) => state.product
	);

	const { cartItems } = useSelector(
		(state) => state.cart
	);

	const { favoritesFlag } = useSelector(
		(state) => state.user
	);


	const categoryButtons = [
		{
			name: '4 Tojásos körettészta',
			filter: {
				packingOf: 4,
				type: 'körettészta',
			},
		},
		{
			name: '4 Tojásos levestészta',
			filter: {
				packingOf: 4,
				type: 'levestészta',
			},
		},
		// {
		//     name: '8 Tojásos körettészta',
		//     filter: {
		//         packingOf: 8,
		//         type: 'körettészta',
		//     },
		// },
		{
			name: '8 Tojásos levestészta',
			filter: {
				packingOf: 8,
				type: 'levestészta',
			},
		},
	];


	// TERMÉKEK BETÖLTÉSE
	useEffect(() => {
		dispatch(getProducts(pageNumber, keyword));
	}, [dispatch, pageNumber, keyword]);


	// KATEGÓRIA SZŰRÉS URL ALAPJÁN
	useEffect(() => {
		if (category) {
			const [packingOf, type] = category.split('-');

			const filtered = products.filter(
				(product) =>
					product.packingOf === Number(packingOf) &&
					product.type === type
			);

			setFilteredProducts(filtered);
		} else {
			setFilteredProducts(products);
		}
	}, [category, products]);


	// AKTÍV KATEGÓRIA BEÁLLÍTÁSA URL ALAPJÁN
	useEffect(() => {
		if (!category) {
			setSelectedCategory(null);
			return;
		}

		const currentCategory = categoryButtons.find(
			(item) =>
				`${item.filter.packingOf}-${item.filter.type}` === category
		);

		setSelectedCategory(currentCategory?.name || null);
	}, [category]);


	// KEDVENCEK FRISSÍTÉSE
	useEffect(() => {
		if (favoritesFlag) {
			dispatch(getUserFavorites());
			dispatch(setFavoritesUpdateFlag(false));
		}
	}, [favoritesFlag, dispatch]);


	// KATEGÓRIA KIVÁLASZTÁS
	const handleCategorySelect = (category) => {
		setSelectedCategory(category.name);

		navigate(
			`/tesztaink/${category.filter.packingOf}-${category.filter.type}`
		);
	};


	// LAKOSSÁGI / LÉDIG SZŰRÉS
	const filteredByType = filteredProducts.filter((item) => {
		if (!selectedType) {
			return true;
		}

		return item.packing === selectedType.toLowerCase();
	});


	return (
		<Box
			mx="auto"
			maxW="8xl"
			p={{ base: 0, lg: 12 }}
			minH="100vh"
			fontFamily="'Poppins', sans-serif"
		>
			<Flex
				borderRadius={{ base: 0, md: '12px' }}
				direction="row"
				align="center"
				justify="space-between"
				backgroundImage={`
                    linear-gradient(
                        90deg,
                        rgba(20,20,20,0.55) 0%,
                        rgba(20,20,20,0.18) 60%,
                        rgba(20,20,20,0.05) 100%
                    ),
                    url('/images/rolunk/LTGy-42.jpg')
                `}
				backgroundSize="cover"
				backgroundPosition={bgPosition}
				px={{ base: 6, md: 14 }}
				minH={{ base: '220px', md: '340px' }}
				position="relative"
				overflow="hidden"
			>
				<Box
					flex="1"
					zIndex={1}
				>
					<Text
						fontFamily="'Playfair Display', serif"
						fontSize={{
							base: '3xl',
							md: '5xl',
							lg: '6xl',
						}}
						lineHeight="1"
						fontWeight="700"
						color="white"
						textShadow="0 3px 12px rgba(0,0,0,0.35)"
					>
						Tésztáink
					</Text>
				</Box>

				<Box
					position="absolute"
					zIndex={0}
					top={{ base: '0', md: '-30px' }}
					left="0"
					width={{ base: '180px', md: '350px' }}
					height={{ base: '180px', md: '350px' }}
					opacity={0.75}
				>
					<svg
						viewBox="0 0 200 200"
						xmlns="http://www.w3.org/2000/svg"
						width="100%"
						height="100%"
					>
						<path
							transform="translate(100 100)"
							fill="#e63e3e"
							d="
                                M35,-57
                                C48.4,-53,64.3,-49.7,74.9,-40.3
                                C85.4,-30.9,90.6,-15.4,90.7,0.1
                                C90.8,15.6,85.8,31.1,75.3,40.4
                                C64.7,49.7,48.4,52.8,35,60.9
                                C21.5,69,10.7,82.1,-1.7,85.1
                                C-14.2,88.1,-28.3,80.9,-39.5,71.4
                                C-50.6,61.9,-58.7,50.2,-66.7,38
                                C-74.8,25.7,-82.9,12.8,-85.5,-1.5
                                C-88.1,-15.9,-85.3,-31.7,-76.7,-43.2
                                C-68.2,-54.6,-53.9,-61.7,-40.2,-65.5
                                C-26.5,-69.3,-13.2,-70,-1.2,-67.9
                                C10.8,-65.8,21.7,-61,35,-57Z
                            "
						/>
					</svg>
				</Box>
			</Flex>
			<Breadcrumb
				ms={{ base: 4, lg: 2 }}
				mt={4}
				fontSize={{ base: 'xs', md: 'sm' }}
				spacing={{ base: '3px', md: '8px' }}
				separator={
					<ChevronRightIcon color="gray.400" />
				}
			>
				<BreadcrumbItem>
					<ReactLink to="/">
						Főoldal
					</ReactLink>
				</BreadcrumbItem>

				<BreadcrumbItem>
					<ReactLink to="/tesztaink">
						Tésztáink
					</ReactLink>
				</BreadcrumbItem>
			</Breadcrumb>
			<Container
				maxW="container.xl"
				px={{ base: 4, md: 6 }}
				py={{ base: 8, md: 12 }}
			>
				<Flex
					gap={{ base: 6, md: 10 }}
					align="flex-start"
				>
					<Box
						position="sticky"
						top="130px"
						width="260px"
						flexShrink={0}
						display={{
							base: 'none',
							md: 'block',
						}}
					>
						<Text

							mb={4}
							color="gray.800"
							fontWeight="600"
							fontSize="2xl"
						>
							Kategóriák
						</Text>

						<Flex
							direction="column"
							gap={1}
						>
							{categoryButtons.map((category) => (
								<Button
									key={category.name}
									onClick={() =>
										handleCategorySelect(category)
									}
									w="100%"
									justifyContent="flex-start"
									variant="ghost"
									fontWeight="500"
									fontSize="sm"
									color={
										selectedCategory === category.name
											? 'red.600'
											: 'gray.700'
									}
									bg={
										selectedCategory === category.name
											? 'red.50'
											: 'transparent'
									}
									borderLeft="3px solid"
									borderColor={
										selectedCategory === category.name
											? 'red.500'
											: 'transparent'
									}
									borderRadius="md"
									position="relative"
									_hover={{
										bg: 'red.50',
										color: 'red.600',
									}}
								>
									{category.name}

									{selectedCategory === category.name && (
										<IoIosCheckmark
											fontSize="24px"
											style={{
												position: 'absolute',
												right: '8px',
											}}
										/>
									)}
								</Button>
							))}
						</Flex>
					</Box>

					<Box
						flex="1"
						minW={0}
					>
						<Box
							display={{
								base: 'block',
								md: 'none',
							}}
							mb={8}
						>
							<Text
								mb={4}
								color="gray.800"
								fontWeight="600"
								fontSize="xl"
								textAlign='center'
							>
								Kategóriák
							</Text>

							<Flex
								direction="column"
								gap={1}
							>
								{categoryButtons.map((category) => (
									<Button
										key={category.name}
										onClick={() =>
											handleCategorySelect(category)
										}
										w="100%"
										justifyContent="flex-start"
										variant="ghost"
										fontWeight="500"
										fontSize="sm"
										color={
											selectedCategory === category.name
												? 'red.600'
												: 'gray.700'
										}
										bg={
											selectedCategory === category.name
												? 'red.50'
												: 'transparent'
										}
										borderLeft="3px solid"
										borderColor={
											selectedCategory === category.name
												? 'red.500'
												: 'transparent'
										}
										borderRadius="md"
										position="relative"
									>
										{category.name}

										{selectedCategory === category.name && (
											<IoIosCheckmark
												fontSize="24px"
												style={{
													position: 'absolute',
													right: '8px',
												}}
											/>
										)}
									</Button>
								))}
							</Flex>
						</Box>
						<Flex
							justify="center"
							mb={8}
						>
							<Flex
								bg="gray.100"
								p={1}
								borderRadius="lg"
								gap={1}
							>

								<Button
									onClick={() =>
										setSelectedType(
											selectedType === 'lakossagi'
												? null
												: 'lakossagi'
										)
									}
									bg={
										selectedType === 'lakossagi'
											? 'white'
											: 'transparent'
									}
									color={
										selectedType === 'lakossagi'
											? 'red.600'
											: 'gray.600'
									}
									boxShadow={
										selectedType === 'lakossagi'
											? 'sm'
											: 'none'
									}
									borderRadius="md"
									fontWeight="500"
									fontSize={{
										base: 'sm',
										md: 'md',
									}}
									minW={{
										base: '120px',
										md: '150px',
									}}
									_hover={{
										bg: 'white',
									}}
								>
									Lakossági
								</Button>


								<Button
									onClick={() =>
										setSelectedType(
											selectedType === 'gyujto'
												? null
												: 'gyujto'
										)
									}
									bg={
										selectedType === 'gyujto'
											? 'white'
											: 'transparent'
									}
									color={
										selectedType === 'gyujto'
											? 'red.600'
											: 'gray.600'
									}
									boxShadow={
										selectedType === 'gyujto'
											? 'sm'
											: 'none'
									}
									borderRadius="md"
									fontWeight="500"
									fontSize={{
										base: 'sm',
										md: 'md',
									}}
									minW={{
										base: '120px',
										md: '150px',
									}}
									_hover={{
										bg: 'white',
									}}
								>
									Lédig
								</Button>

							</Flex>
						</Flex>
						{loading ? (
							<Center minH="50vh">
								<Spinner
									thickness="3px"
									speed="0.65s"
									emptyColor="gray.200"
									color="red.500"
									size="xl"
								/>
							</Center>

						) : error ? (

							<Alert
								status="error"
								borderRadius="md"
							>
								<AlertIcon />

								<Box>
									<AlertTitle>
										Hiba történt
									</AlertTitle>

									<AlertDescription>
										{error}
									</AlertDescription>
								</Box>
							</Alert>

						) : filteredByType.length === 0 ? (

							<Center
								minH="45vh"
								flexDirection="column"
								textAlign="center"
								px={4}
							>
								<Text
									fontSize={{
										base: 'md',
										md: 'lg',
									}}
									fontWeight="600"
									color="gray.700"
								>
									Nincs elérhető termék ebben a kategóriában.
								</Text>

								{selectedType === 'gyujto' && (
									<Text
										mt={4}
										maxW="500px"
										fontSize={{
											base: 'sm',
											md: 'md',
										}}
										color="gray.500"
										lineHeight="1.7"
									>
										A lédig csomagolása 10 kg-os
										polietilén zsákban történik.
									</Text>
								)}
							</Center>

						) : (

							<Wrap
								spacing={{
									base: '16px',
									md: '24px',
									lg: '28px',
								}}
								justify="center"
								align="stretch"
							>
								{filteredByType.map((product) => (
									<WrapItem
										key={product._id}
									>
										<ProductCard
											product={product}
											loading={loading}
											cartItems={cartItems}
										/>
									</WrapItem>
								))}
							</Wrap>

						)}

					</Box>
				</Flex>
			</Container>

		</Box>
	);
};

export default ProductsScreen;